// Wrappers for /api/v1/notifications (bigroi-hrms-php/routes/api/notifications.php).
import { api } from './client'
import type { PaginationEnvelope } from '@/types/common'

export interface AppNotification {
  id: string
  type: string
  title: string
  body: string | null
  data: Record<string, unknown> | null
  is_read: boolean
  read_at: string | null
  created_at: string | null
}

export interface NotificationPage {
  items: AppNotification[]
  pagination?: PaginationEnvelope
}

export const notificationKeys = {
  all: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  list: (tab: 'unread' | 'all', perPage: number, page = 1) => ['notifications', 'list', tab, perPage, page] as const,
}

export const notificationsApi = {
  list: async (params: { unreadOnly?: boolean; perPage?: number; page?: number } = {}): Promise<NotificationPage> => {
    const r = await api.get('/api/v1/notifications', {
      params: {
        per_page: params.perPage ?? 10,
        page: params.page ?? 1,
        ...(params.unreadOnly ? { is_read: 'false' } : {}),
      },
    })
    const d = r.data as NotificationPage | AppNotification[]
    return Array.isArray(d) ? { items: d } : { items: d?.items ?? [], pagination: d?.pagination }
  },
  unreadCount: async (): Promise<number> => {
    const r = await api.get('/api/v1/notifications/unread-count')
    const d = r.data as { count?: number } | number
    return typeof d === 'number' ? d : (d?.count ?? 0)
  },
  /** Returns the updated row; callers verify `is_read` instead of assuming the write landed. */
  markRead: async (id: string): Promise<AppNotification> => {
    const r = await api.post(`/api/v1/notifications/${id}/read`, {})
    return r.data as AppNotification
  },
  /** Returns how many rows the server actually marked. */
  markAllRead: async (): Promise<number> => {
    const r = await api.post('/api/v1/notifications/read-all', {})
    return Number((r.data as { marked?: number })?.marked ?? 0)
  },
}

/**
 * Where a notification leads. Every notification has a permanent destination (spec 21
 * §3.6.4, "no dead ends"): an explicit deep link when the API sends one, otherwise the
 * record named in `data`, otherwise the notifications page itself.
 */
export function notificationHref(n: AppNotification): string {
  const d = n.data ?? {}
  const str = (k: string) => (typeof d[k] === 'string' && d[k] ? (d[k] as string) : null)
  const explicit = str('deep_link') ?? str('url') ?? str('link')
  if (explicit && explicit.startsWith('/')) return explicit
  // Requests waiting for the RECIPIENT's decision open the approvals inbox, not the requester's page.
  if (/_REQUESTED$|_SUBMITTED$|^LEAVE_MANAGER_APPROVED$/.test(n.type) && !/^PROFILE_CHANGE_SUBMITTED$/.test(n.type)) {
    const t = n.type.startsWith('LEAVE') ? 'leave' : n.type.startsWith('REGULARI') ? 'regularisation' : null
    return t ? `/approvals?type=${t}` : '/approvals'
  }
  if (n.type === 'PROFILE_CHANGE_SUBMITTED') return '/profile-approvals'
  if (str('leave_request_id')) return `/ess/leaves/${str('leave_request_id')}`
  if (str('policy_id')) return `/ess/policies/${str('policy_id')}`
  if (str('asset_id')) return n.type === 'ASSET_RETURNED' ? '/ess/assets' : `/ess/assets/${str('asset_id')}`
  if (str('announcement_id') || n.type === 'ANNOUNCEMENT') return '/feed'
  if (str('ticket_id')) return `/helpdesk/${str('ticket_id')}`
  if (n.type === 'PAYSLIP_READY' || str('payroll_run_id')) return '/ess/payslips'
  if (str('review_id')) return `/performance/reviews/${str('review_id')}`
  if (str('review_cycle_id')) return '/performance'
  if (str('reimbursement_id')) return `/reimbursements/${str('reimbursement_id')}`
  if (str('loan_id')) return `/loans/${str('loan_id')}`
  return '/notifications'
}
